import {useContext, useEffect} from 'react';
import {ActivityScope} from "../../types/ActivityScope";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";

export const ActivityScopesListPage = function () {
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.settings.pathSegment}/activity-scopes`;
    const dataStore = useRenderableDataStore({
        activityScopesList: [],
        loading: true,
    });

    useEffect(function () {
        reloadList();
    }, []);

    function reloadList() {
        dataStore.updateAndRender({loading: true});
        ApiUtility.getActivityScopes()
            .then(function (activityScopesList) {
                dataStore.updateAndRender({
                    activityScopesList: activityScopesList,
                    loading: false,
                });
            })
            .catch(function (error) {
                dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the scopes list.`);
            });
    }

    function goToAddActivityScopePage() {
        context.navigator.navigateTo(`${urlBase}/add`);
    }

    function goToUpdateActivityScopePage(activityScope: ActivityScope) {
        context.navigator.navigateTo(`${urlBase}/${activityScope.id}`);
    }

    function renderActivityScopesListDisplay(activityScopesList: ActivityScope[]) {
        let activityScopesListDisplay = [];

        for (let i = 0; i < activityScopesList.length; i++) {
            const activityScope = activityScopesList[i];
            const materialCount = activityScope.activity_material_ids.length;
            const materialCountSummary = materialCount === 0
                ? "No scoped materials"
                : `${materialCount} scoped material${materialCount === 1 ? '' : 's'}`;
            const deactivatedRowClass = activityScope.active ? '' : ' deactivated';
            const inactiveSuffix = activityScope.active ? '' : ' (Inactive)';

            activityScopesListDisplay.push(
                <div
                    key={i + 1}
                    className={`common-list-row activity-scopes-list-row${deactivatedRowClass}`}
                    onClick={() => goToUpdateActivityScopePage(activityScope)}
                    data-activity-scope-id={activityScope.id}
                >
                    <div className="common-list-row-content">
                        <div className="common-list-row-ticket-edge">
                            <div className="common-list-row-ticket-circle"/>
                        </div>
                        <div className="common-list-row-main">{activityScope.name}{inactiveSuffix}</div>
                        <div className="common-list-row-details">
                            <div className="activity-scopes-list-row-material-count">
                                {materialCountSummary}
                            </div>
                        </div>
                        <div className="common-list-row-action-indicators">
                            <div className="activity-scopes-list-row-edit-action-cell">EDIT</div>
                            <div className="common-list-row-pointer-cell">
                                <img
                                    src="/static/images/arrow-right-tiny-green.svg"
                                    className="common-list-row-pointer-icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return activityScopesListDisplay;
    }

    return (
        <div className="common-list-page activity-scopes-list-page">
            <div className="common-list-page-header">
                <div className="common-list-page-header-content">
                    <div className="common-list-page-header-content-left">
                        <div className="common-list-page-header-text">Scopes</div>
                    </div>
                    <div className="common-list-page-header-content-right">
                        <div
                            className="icon-button add-button"
                            onClick={goToAddActivityScopePage}
                        >
                            <div className="icon-button-content">
                                <img
                                    className="icon-button-image"
                                    src="/static/images/plus-green.svg"
                                />
                                <div className="icon-button-text">Add Scope</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="common-list-page-content">
                <ConditionalRenderer
                    if={dataStore.data().loading}
                    renderer={() => <OverlayLoadingIndicator/>}
                />
                {(() => {
                    if (dataStore.data().activityScopesList.length > 0) {
                        return (
                            <div className="common-list-content activity-scopes-list-content">
                                {renderActivityScopesListDisplay(dataStore.data().activityScopesList)}
                            </div>
                        );
                    } else if (!dataStore.data().loading) {
                        return (
                            <div className="common-empty-list-message-container">
                                <div className="common-empty-list-message">No scopes found.</div>
                            </div>
                        );
                    }
                })()}
            </div>
        </div>
    );
};
